import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Container, Typography, Button, Stack, Chip, Grid, Skeleton, IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBackOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AppHeader from "../components/AppHeader";
import AppFooter from "../components/AppFooter";
import api, { tokens } from "../api";

/*
 * What a plan is, and what it costs.
 *
 * One number per plan: how many tokens a day. There used to be a list of features here -
 * mobile, knowledge-base documents, alarm actions - and none of it was enforced anywhere,
 * so the page was selling things the product did not check. What a customer actually buys
 * is how much the assistant may think per day.
 *
 * Read from the server, not written here: the allowance and the price are edited in the
 * console, and a sales page that disagrees with the meter is worse than no sales page.
 */


/*
 * Printed in every card, identically, because every plan really does include all of it -
 * the only thing being sold is the daily allowance. Repeating it is the point: a customer
 * comparing three columns should see that nothing is being withheld from the cheap one.
 *
 * Only things the product actually does. The list that used to be here promised mobile
 * apps, document limits and alarm actions that nothing anywhere enforced.
 */
const INCLUDED = [
    "Your own tags, devices, alarms and reports",
    "Ask in English, Hindi or Hinglish",
    "Runs on the plant's own server - data stays there",
    "Every user on that plant included",
];


export default function Plans() {
    const navigate = useNavigate();
    const [account, setAccount] = useState(null);
    const [plans, setPlans] = useState(null);

    useEffect(() => {
        api.get("/me")
            .then(({ data }) => setAccount(data.account))
            .catch(() => { tokens.account.clear(); navigate("/"); });

        api.get("/plans")
            .then(({ data }) => setPlans(data.data || []))
            .catch(() => setPlans([]));
    }, [navigate]);

    return (
        <Box sx={{
            minHeight: "100vh", color: "#fff",
            display: "flex", flexDirection: "column",
            background:
                "radial-gradient(900px 520px at 12% 0%, rgba(46,125,32,.32), transparent 62%),"
                + "radial-gradient(760px 540px at 88% 100%, rgba(20,90,60,.28), transparent 64%), #07120B",
        }}>
            <AppHeader account={account} />

            <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, md: 4 } }}>
                {/* Beside the title, like the Profile page: this page is reached from a
                    button, and the way out should not be a scroll away. */}
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 1 }}>
                    <IconButton onClick={() => navigate("/account")}
                        sx={{
                            color: "rgba(255,255,255,.75)",
                            border: "1px solid rgba(255,255,255,.14)",
                            bgcolor: "rgba(255,255,255,.04)",
                            "&:hover": { bgcolor: "rgba(255,255,255,.09)" },
                        }}>
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <Typography sx={{ fontWeight: 800, fontSize: 30, letterSpacing: -0.6 }}>Plans</Typography>
                </Stack>


                {!account || !plans ? (
                    <Skeleton variant="rounded" height={380} sx={{ bgcolor: "rgba(255,255,255,.06)" }} />
                ) : (
                    <Grid container spacing={2.5}>
                        {plans.map((p) => {
                            const free = !p.priceMonthly;
                            const popular = p.code === "plus";
                            return (
                                <Grid size={{ xs: 12, md: 4 }} key={p.code}>
                                    <Box sx={{
                                        height: "100%", p: 3, borderRadius: 4,
                                        display: "flex", flexDirection: "column",
                                        bgcolor: popular ? "rgba(46,125,32,.14)" : "rgba(255,255,255,.05)",
                                        border: `1px solid ${popular ? "rgba(123,217,95,.4)" : "rgba(255,255,255,.12)"}`,
                                    }}>
                                        <Stack direction="row" spacing={1.2} sx={{ alignItems: "center" }}>
                                            <Typography sx={{ fontWeight: 700, fontSize: 19 }}>{p.name}</Typography>
                                            {popular && <Chip size="small" label="Popular" sx={{
                                                bgcolor: "rgba(123,217,95,.18)", color: "#9FE08C",
                                                border: "1px solid rgba(123,217,95,.4)", fontWeight: 600,
                                            }} />}
                                        </Stack>

                                        <Typography sx={{ fontWeight: 800, fontSize: 28, mt: .5, whiteSpace: "nowrap" }}>
                                            {free ? "Free" : `₹${p.priceMonthly.toLocaleString("en-IN")}`}
                                            {!free && (
                                                <Box component="span" sx={{
                                                    fontSize: 14, fontWeight: 500, color: "rgba(255,255,255,.5)",
                                                }}> / month</Box>
                                            )}
                                        </Typography>

                                        {/* The allowance first and in bigger type - it is the
                                            only line that differs between these three cards. */}
                                        <Box sx={{ mt: 3, flexGrow: 1 }}>
                                            <Stack direction="row" spacing={1.2} sx={{ alignItems: "flex-start" }}>
                                                <CheckCircleIcon sx={{ fontSize: 18, color: "#7BD95F", mt: .3, flexShrink: 0 }} />
                                                <Box>
                                                    <Typography sx={{ fontSize: 15.5, fontWeight: 700 }}>
                                                        {p.apiLimitPerDay.toLocaleString("en-IN")} tokens a day
                                                    </Typography>
                                                </Box>
                                            </Stack>

                                            <Stack spacing={1.3} sx={{ mt: 2.2 }}>
                                                {INCLUDED.map((t) => (
                                                    <Stack key={t} direction="row" spacing={1.2}
                                                        sx={{ alignItems: "flex-start" }}>
                                                        <CheckCircleIcon sx={{
                                                            fontSize: 18, color: "#7BD95F", mt: .2, flexShrink: 0,
                                                        }} />
                                                        <Typography sx={{ fontSize: 14, color: "rgba(255,255,255,.8)" }}>
                                                            {t}
                                                        </Typography>
                                                    </Stack>
                                                ))}
                                            </Stack>
                                        </Box>

                                        <Button fullWidth variant={popular ? "contained" : "outlined"}
                                            onClick={() => navigate("/account")}
                                            sx={{
                                                mt: 3, borderRadius: 999, py: 1.1,
                                                ...(popular ? {} : {
                                                    color: "#fff", borderColor: "rgba(255,255,255,.28)",
                                                }),
                                            }}>
                                            {free ? "Included" : `Choose ${p.name}`}
                                        </Button>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}

                {/* The plan belongs to a token, not to the account: one customer can run
                    three plants and pay for the busy one only. */}
                <Typography sx={{ color: "rgba(255,255,255,.45)", fontSize: 13.5, mt: 2.5 }}>
                    A plan applies to one plant. Pick the plant on your Tokens page and upgrade it
                    there - billing is monthly and renews on its own until you cancel.
                </Typography>


            </Container>

            <AppFooter />
        </Box>
    );
}
